import { onMounted, onBeforeUnmount, watch, nextTick } from "vue";

// Observes `sentinelRef` (a template ref on a marker element at the end of a
// list) and calls `loadMore` once it scrolls into view. Caller is responsible
// for guarding against concurrent/duplicate calls (e.g. an isLoadingMore flag).
export function useInfiniteScroll(sentinelRef, loadMore, options = {}) {
  let observer = null;
  let isPumping = false;
  let lastIsIntersecting = false;

  function teardown() {
    observer?.disconnect();
    observer = null;
  }

  // A single page may still leave a short container (e.g. a narrow side
  // panel with few items) without enough content to scroll — the sentinel
  // then never actually leaves the viewport, so IntersectionObserver (which
  // only fires on a genuine visibility transition, not merely "still
  // visible") won't ask for the next page on its own. Keep pumping while the
  // sentinel remains visible and each call still makes progress; stop once
  // it stops moving (list exhausted) or the observer reports it's scrolled
  // out of view.
  async function pump() {
    if (isPumping) return;
    isPumping = true;
    try {
      while (lastIsIntersecting && sentinelRef.value) {
        const before = sentinelRef.value.getBoundingClientRect().top;
        await loadMore();
        await nextTick();
        if (!sentinelRef.value) break;
        const after = sentinelRef.value.getBoundingClientRect().top;
        if (after === before) break; // loadMore() was a no-op — nothing left to fetch
      }
    } finally {
      isPumping = false;
    }
  }

  function setup() {
    teardown();
    if (!sentinelRef.value) return;
    observer = new IntersectionObserver(
      (entries) => {
        lastIsIntersecting = !!entries[0]?.isIntersecting;
        if (lastIsIntersecting) pump();
      },
      { root: options.root ?? null, rootMargin: options.rootMargin ?? "200px" }
    );
    observer.observe(sentinelRef.value);
  }

  onMounted(setup);
  onBeforeUnmount(teardown);
  watch(sentinelRef, setup);
}
